import React, {
  useState,
  useCallback,
  useImperativeHandle,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { HelpCircle } from "lucide-react";
import styles from "./UsageInput.module.css";

// Pure helper functions (moved outside component to reduce complexity)
const isValidNumericInput = (value) => {
  for (const element of value) {
    if (element < "0" || element > "9") {
      return false;
    }
  }
  return true;
};

const validateValueUnderMax = (value) => {
  if (value && value !== ".") {
    const numValue = Number.parseFloat(value);
    if (numValue > 999999) {
      return {
        valid: false,
        error:
          "Your consumption must be under 999,999kwh per year. If it's higher than this, please call us to discuss your energy needs",
      };
    }
  }
  return { valid: true, error: "" };
};

const isDayNightOrPeakMeter = (meterType) =>
  meterType === "Day/Night" || meterType === "Peak/Off-Peak";

const getDescriptionText = (selectedFuel) =>
  selectedFuel === "electricity"
    ? "Please enter your consumption below and choose a frequency option from the dropdown. Telling us about your energy use in kWh will give a more accurate quote. When you tell us about your spend, we make assumptions about your current tariff and discounts to work out your energy use."
    : "Please enter either your energy consumption or spend in pounds, and choose an option from the dropdown. Telling us about your energy use in kWh will give a more accurate quote. When you tell us about your spend, we make assumptions about your current tariff and discounts to work out your energy use.";

// Sub-component for usage type radio buttons
const UsageTypeSelector = ({ usageType, onUsageTypeChange }) => (
  <div className={styles.usageTypeContainer}>
    <label className={styles.radioLabel}>
      <input
        type="radio"
        name="usageType"
        value="kwh"
        checked={usageType === "kwh"}
        onChange={(e) => onUsageTypeChange(e.target.value)}
        className={styles.radioInput}
      />
      <span className={styles.radioText}>kWh usage</span>
    </label>
    <label className={styles.radioLabel}>
      <input
        type="radio"
        name="usageType"
        value="spend"
        checked={usageType === "spend"}
        onChange={(e) => onUsageTypeChange(e.target.value)}
        className={styles.radioInput}
      />
      <span className={styles.radioText}>£ spend</span>
    </label>
  </div>
);

UsageTypeSelector.propTypes = {
  usageType: PropTypes.string.isRequired,
  onUsageTypeChange: PropTypes.func.isRequired,
};

// Sub-component for single rate billing section
const SingleRateBillingSection = ({
  singleRateBilling,
  onSingleRateBillingChange,
  showMeterInfo,
  onToggleMeterInfo,
  selectedFuel,
}) => (
  <>
    <div className={"flex items-center mt-4 mb-2 "}>
      <label className={styles.checkboxLabel}>
        <input
          type="checkbox"
          checked={singleRateBilling}
          onChange={(e) => onSingleRateBillingChange(e.target.checked)}
          className={styles.checkbox}
          aria-describedby={showMeterInfo ? "single-rate-info" : undefined}
        />
        <span className={styles.checkboxText}>Single-rate billing</span>
      </label>
      <button
        type="button"
        className={styles.helpIcon}
        onClick={(e) => {
          e.stopPropagation();
          onToggleMeterInfo();
        }}
        aria-label={`${showMeterInfo ? "Hide" : "Show"} single-rate billing help information`}
        aria-expanded={showMeterInfo}
        aria-controls="single-rate-info"
      >
        <HelpCircle size={20} />
      </button>
    </div>
    {showMeterInfo && (
      <div
        id="single-rate-info"
        role="alert"
        className={styles.singleRateInfoBox}
      >
        If you&apos;re unsure about your two-rate time bandings, click{" "}
        <a
          target="_blank"
          rel="noreferrer"
          href="https://engie.co.uk/wp-content/uploads/2016/08/tprreferencesheet-1.pdf"
          className={styles.infoLink}
        >
          here.
        </a>
      </div>
    )}
    {selectedFuel === "electricity" && (
      <p className={styles.description}>
        Our records indicate you have a two-rate meter installed. If you wish to
        be billed at a single rate price, please tick the single-rate price box
        above. By choosing a single-rate price, both rates will be charged at
        the same unit price.
      </p>
    )}
  </>
);

SingleRateBillingSection.propTypes = {
  singleRateBilling: PropTypes.bool,
  onSingleRateBillingChange: PropTypes.func,
  showMeterInfo: PropTypes.bool.isRequired,
  onToggleMeterInfo: PropTypes.func.isRequired,
  selectedFuel: PropTypes.string.isRequired,
};

// Sub-component for day/night input fields
const DayNightInputs = ({
  detectedMeterType,
  usageType,
  dayValue,
  nightValue,
  onDayChange,
  onNightChange,
  validationError,
}) => {
  const isDayNight = detectedMeterType === "Day/Night";
  const dayLabel = isDayNight ? "Day usage" : "Peak usage";
  const nightLabel = isDayNight ? "Night usage" : "Off-Peak usage";
  const dayPlaceholder = isDayNight ? "Day" : "Peak";
  const nightPlaceholder = isDayNight ? "Night" : "Off-Peak";
  const unitLabel = usageType === "kwh" ? "kWh " : "£";
  const inputClassName = `${styles.usageInput} ${validationError ? styles.inputError : ""}`;

  return (
    <div>
      <div className={styles.inputWrapper}>
        <label htmlFor="day-peak-input" className="sr-only">
          {dayLabel}
        </label>
        <div className={styles.unitLabel} aria-hidden="true">
          {unitLabel}
        </div>
        <input
          id="day-peak-input"
          type="text"
          placeholder={dayPlaceholder}
          value={dayValue}
          onChange={onDayChange}
          className={inputClassName}
          aria-invalid={!!validationError}
          aria-describedby={validationError ? "usage-error" : undefined}
        />
        {validationError && (
          <div className={styles.errorIcon} aria-hidden="true">
            !
          </div>
        )}
      </div>
      <div className={styles.inputWrapper}>
        <label htmlFor="night-offpeak-input" className="sr-only">
          {nightLabel}
        </label>
        <div className={styles.unitLabel} aria-hidden="true">
          {unitLabel}
        </div>
        <input
          id="night-offpeak-input"
          type="text"
          placeholder={nightPlaceholder}
          value={nightValue}
          onChange={onNightChange}
          className={inputClassName}
          aria-invalid={!!validationError}
          aria-describedby={validationError ? "usage-error" : undefined}
        />
        {validationError && (
          <div className={styles.errorIcon} aria-hidden="true">
            !
          </div>
        )}
      </div>
    </div>
  );
};

DayNightInputs.propTypes = {
  detectedMeterType: PropTypes.string,
  usageType: PropTypes.string.isRequired,
  dayValue: PropTypes.string.isRequired,
  nightValue: PropTypes.string.isRequired,
  onDayChange: PropTypes.func.isRequired,
  onNightChange: PropTypes.func.isRequired,
  validationError: PropTypes.string,
};

// Sub-component for single usage input
const SingleUsageInput = ({
  usageType,
  usageValue,
  onChange,
  validationError,
}) => {
  const inputLabel =
    usageType === "kwh" ? "Energy usage in kWh" : "Energy spend in pounds";
  const unitLabel = usageType === "kwh" ? "kWh " : "£";

  return (
    <div className={styles.inputWrapper}>
      <label htmlFor="usage-input" className="sr-only">
        {inputLabel}
      </label>
      <div className={styles.unitLabel} aria-hidden="true">
        {unitLabel}
      </div>
      <input
        id="usage-input"
        type="text"
        value={usageValue}
        onChange={onChange}
        className={`${styles.usageInput} ${validationError ? styles.inputError : ""}`}
        aria-invalid={!!validationError}
        aria-describedby={validationError ? "usage-error" : undefined}
      />
      {validationError && (
        <div className={styles.errorIcon} aria-hidden="true">
          !
        </div>
      )}
    </div>
  );
};

SingleUsageInput.propTypes = {
  usageType: PropTypes.string.isRequired,
  usageValue: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  validationError: PropTypes.string,
};

// Main component
const UsageInput = React.forwardRef(
  (
    {
      usageType,
      onUsageTypeChange,
      usageValue,
      onUsageValueChange,
      usagePeriod,
      onUsagePeriodChange,
      onValidationChange,
      selectedFuel,
      detectedMeterType,
      singleRateBilling,
      onSingleRateBillingChange,
      initialDayValue,
      initialNightValue,
    },
    ref,
  ) => {
    const [validationError, setValidationError] = useState("");
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const [showMeterInfo, setShowMeterInfo] = useState(false);
    const [dayValue, setDayValue] = useState(initialDayValue || "");
    const [nightValue, setNightValue] = useState(initialNightValue || "");

    useEffect(() => {
      if (initialDayValue !== undefined) setDayValue(initialDayValue);
      if (initialNightValue !== undefined) setNightValue(initialNightValue);
    }, [initialDayValue, initialNightValue]);

    const setValidationResult = useCallback(
      (isValid, error = "") => {
        setValidationError(error);
        if (onValidationChange) onValidationChange(isValid, error);
        return isValid;
      },
      [onValidationChange],
    );

    const validateUsageTypeSelected = useCallback(() => {
      if (!usageType) {
        return {
          valid: false,
          error: "Please select either kWh usage or £ spend",
        };
      }
      return { valid: true, error: "" };
    }, [usageType]);

    const validateDayNightInputsNotEmpty = useCallback(() => {
      const dayEmpty = !dayValue || dayValue.trim() === "";
      const nightEmpty = !nightValue || nightValue.trim() === "";
      if (dayEmpty || nightEmpty) {
        return { valid: false, error: "Please enter your consumption" };
      }
      return { valid: true, error: "" };
    }, [dayValue, nightValue]);

    const validateSingleInputNotEmpty = useCallback(() => {
      if (!usageValue || usageValue.trim() === "") {
        return { valid: false, error: "Please enter your consumption" };
      }
      return { valid: true, error: "" };
    }, [usageValue]);

    const validateDayNightValues = useCallback(() => {
      const dayResult = validateValueUnderMax(dayValue);
      if (!dayResult.valid) return dayResult;

      const nightResult = validateValueUnderMax(nightValue);
      if (!nightResult.valid) return nightResult;

      return { valid: true, error: "" };
    }, [dayValue, nightValue]);

    const validateSingleUsageValue = useCallback(() => {
      if (!isValidNumericInput(usageValue)) {
        return { valid: false, error: "Please enter numbers only" };
      }

      if ((usageValue.match(/\./g) || []).length > 1) {
        return { valid: false, error: "Please enter a valid number" };
      }

      if (usageValue !== ".") {
        const numValue = Number.parseFloat(usageValue);

        if (numValue > 999999) {
          return {
            valid: false,
            error:
              "Your consumption must be under 999,999kwh per year. If it's higher than this, please call us to discuss your energy needs",
          };
        }

        if (numValue < 0) {
          return { valid: false, error: "Value cannot be negative" };
        }
      }

      return { valid: true, error: "" };
    }, [usageValue]);

    const validateForm = useCallback(() => {
      setHasSubmitted(true);

      // Check if usage type is selected
      const usageTypeResult = validateUsageTypeSelected();
      if (!usageTypeResult.valid) {
        return setValidationResult(false, usageTypeResult.error);
      }

      // Validate inputs are not empty
      if (isDayNightOrPeakMeter(detectedMeterType) && usageType === "kwh") {
        const emptyResult = validateDayNightInputsNotEmpty();
        if (!emptyResult.valid) {
          return setValidationResult(false, emptyResult.error);
        }
      } else {
        const emptyResult = validateSingleInputNotEmpty();
        if (!emptyResult.valid) {
          return setValidationResult(false, emptyResult.error);
        }
      }

      // Validate values are within limits
      if (detectedMeterType === "Day/Night") {
        const dayNightResult = validateDayNightValues();
        if (!dayNightResult.valid) {
          return setValidationResult(false, dayNightResult.error);
        }
      } else {
        const singleResult = validateSingleUsageValue();
        if (!singleResult.valid) {
          return setValidationResult(false, singleResult.error);
        }
      }

      // All validation passed
      return setValidationResult(true, "");
    }, [
      usageType,
      detectedMeterType,
      validateUsageTypeSelected,
      validateDayNightInputsNotEmpty,
      validateSingleInputNotEmpty,
      validateDayNightValues,
      validateSingleUsageValue,
      setValidationResult,
    ]);

    // Expose validation function and values to parent via ref
    useImperativeHandle(ref, () => {
      const refObject = {
        validate: validateForm,
        dayValue,
        nightValue,
      };
      return refObject;
    }, [validateForm, dayValue, nightValue]);

    const handleUsageValueChange = (e) => {
      const value = e.target.value;

      // Clear error for empty string
      if (value === "") {
        onUsageValueChange(value);
        setValidationError("");
        return;
      }

      // Block non-numeric input - don't update if invalid
      if (!isValidNumericInput(value)) {
        return;
      }

      // Update the value only if it's valid
      onUsageValueChange(value);

      // Check for multiple decimal points and show error but don't block
      if ((value.match(/\./g) || []).length > 1) {
        setValidationError("Please enter your consumption");
        return;
      }

      // Convert to number for validation (only if not just a decimal point)
      if (value !== ".") {
        const numValue = Number.parseFloat(value);

        // Check if value is greater than 120000 and show error but don't block
        if (numValue > 999999) {
          setValidationError(
            "Your consumption must be under 999,999kwh per year. If it's higher than this, please call us to discuss your energy needs",
          );
          return;
        }

        // Check if value is negative and show error but don't block
        if (numValue < 0) {
          setValidationError("Value cannot be negative");
          return;
        }
      }

      // Clear any previous validation errors if input is valid
      setValidationError("");

      // If form has been submitted, revalidate to clear errors immediately
      if (hasSubmitted && onValidationChange) {
        validateForm();
      }
    };

    const handleUsageTypeChange = (value) => {
      onUsageTypeChange(value);

      // If form has been submitted, revalidate to clear errors immediately
      if (hasSubmitted) {
        // Clear validation error when user selects a usage type
        if (
          value &&
          validationError === "Please select either kWh usage or £ spend"
        ) {
          setValidationError("");
          if (onValidationChange) onValidationChange(true, "");
        }
      }
    };

    const validateDayNightOnChange = (dayVal, nightVal) => {
      const dayResult = validateValueUnderMax(dayVal);
      if (!dayResult.valid) {
        setValidationError(dayResult.error);
        return;
      }

      const nightResult = validateValueUnderMax(nightVal);
      if (!nightResult.valid) {
        setValidationError(nightResult.error);
        return;
      }

      // Clear errors if both values are valid
      setValidationError("");
    };

    const handleDayValueChange = (e) => {
      const value = e.target.value;

      // Block non-numeric input - don't update if invalid
      if (!isValidNumericInput(value)) {
        return;
      }

      // Update the value
      setDayValue(value);

      // Validate both day and night values
      validateDayNightOnChange(value, nightValue);
    };

    const handleNightValueChange = (e) => {
      const value = e.target.value;

      // Block non-numeric input - don't update if invalid
      if (!isValidNumericInput(value)) {
        return;
      }

      // Update the value
      setNightValue(value);

      // Validate both day and night values
      validateDayNightOnChange(dayValue, value);
    };

    const showDayNightInputs =
      isDayNightOrPeakMeter(detectedMeterType) && usageType === "kwh";
    const fuelTypeLabel =
      selectedFuel === "electricity" ? "electricity" : "gas";
    const usageLabel = usageType === "kwh" ? "consumption" : "usage";

    return (
      <div className={styles.container}>
        <div className={styles.headerSection}>
          <div className={styles.header}>
            <h3 className={styles.title}>
              Please enter your {fuelTypeLabel} {usageLabel}
            </h3>
          </div>
          <p className={styles.description}>
            {getDescriptionText(selectedFuel)}
          </p>
        </div>

        <UsageTypeSelector
          usageType={usageType}
          onUsageTypeChange={handleUsageTypeChange}
        />

        {isDayNightOrPeakMeter(detectedMeterType) && (
          <SingleRateBillingSection
            singleRateBilling={singleRateBilling}
            onSingleRateBillingChange={onSingleRateBillingChange}
            showMeterInfo={showMeterInfo}
            onToggleMeterInfo={() => setShowMeterInfo(!showMeterInfo)}
            selectedFuel={selectedFuel}
          />
        )}

        <div className={styles.usageInputContainer}>
          {showDayNightInputs ? (
            <DayNightInputs
              detectedMeterType={detectedMeterType}
              usageType={usageType}
              dayValue={dayValue}
              nightValue={nightValue}
              onDayChange={handleDayValueChange}
              onNightChange={handleNightValueChange}
              validationError={validationError}
            />
          ) : (
            <SingleUsageInput
              usageType={usageType}
              usageValue={usageValue}
              onChange={handleUsageValueChange}
              validationError={validationError}
            />
          )}

          <label htmlFor="usage-period" className="sr-only">
            Usage period
          </label>
          <select
            id="usage-period"
            value={usagePeriod}
            onChange={(e) => onUsagePeriodChange(e.target.value)}
            className={styles.periodSelect}
          >
            <option value="Month">Month</option>
            <option value="Quarter">Quarter</option>
            <option value="6Months">6 Months</option>
            <option value="12Months">12 Months</option>
          </select>
        </div>

        {validationError && (
          <div
            id="usage-error"
            className={styles.errorMessage}
            role="alert"
            aria-live="polite"
          >
            {validationError}
          </div>
        )}
      </div>
    );
  },
);

UsageInput.propTypes = {
  usageType: PropTypes.string.isRequired,
  onUsageTypeChange: PropTypes.func.isRequired,
  usageValue: PropTypes.string.isRequired,
  onUsageValueChange: PropTypes.func.isRequired,
  usagePeriod: PropTypes.string.isRequired,
  onUsagePeriodChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  selectedFuel: PropTypes.string.isRequired,
  detectedMeterType: PropTypes.string,
  singleRateBilling: PropTypes.bool,
  onSingleRateBillingChange: PropTypes.func,
  initialDayValue: PropTypes.string,
  initialNightValue: PropTypes.string,
};

UsageInput.displayName = "UsageInput";

export default UsageInput;
